import { CopyTigerRunApprovals } from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { CopyTigerAdFlagged, CopyTigerRun } from './runReviewUtils';

// SYNTHETIC fixture for the run review page (open a run URL with ?mock=1). It mirrors the shape and
// counts of a real producer sample (7 ads; ~3 current vs 12-15 generated headlines) with the brand
// swapped for a made-up one. Campaign 20000001 carries names in context (older runs); the rest carry
// them in ad.names, like current runs.
// Real runs can carry hundreds of keywords per ad; the Bing ad gets 40 so the '+N more' fold shows.
const MANY_KEYWORDS = ['boat', 'pontoon', 'bass boat', 'jon boat', 'sailboat', 'kayak']
  .flatMap(boat =>
    ['cover', 'covers', 'cover custom', 'cover waterproof', 'cover sale', 'cover heavy duty'].map(
      kind => `${boat} ${kind}`
    )
  )
  .concat(['marine covers', 'boat tarp', 'mooring cover', 'trailerable cover'])
  .join(',');

// The producer's 'why flagged' sample (bpm-lambdas #8960): on the anchor-kit ad with a warning (internal
// story) and on a client-visible ad without one (client stories). The other ads have none.
const MOCK_FLAGGED: CopyTigerAdFlagged = {
  summary:
    '$1,240 spend ($1,140 over the $100 minimum); 0 conversions (at or below 1); POOR ad strength; 4.1% of flagged spend, 6.3% of flagged impressions',
  reasons: [
    { metric: 'spend', label: 'Spend', value: 1240, threshold: 100, delta: 1140, deltaPct: 1140 },
    {
      metric: 'conversions',
      label: 'Conversions',
      value: 0,
      threshold: 1,
      delta: -1,
      deltaPct: -100
    },
    {
      metric: 'conversionsValue',
      label: 'Conversion value',
      value: 0,
      threshold: 50,
      delta: -50,
      deltaPct: -100
    },
    { metric: 'adStrength', label: 'Ad strength', value: 'POOR' },
    {
      metric: 'activeDays',
      label: 'Days active',
      value: 212,
      threshold: 30,
      delta: 182,
      deltaPct: 606.7
    }
  ]
};
const MOCK_WARNINGS = ['missing protected brand copy: Acme Marine®'];

export const MOCK_RUN: CopyTigerRun = {
  schemaVersion: 1,
  runId: 'mock-run',
  company: 'acmemarine',
  createdAt: '2026-10-06T14:00:00Z',
  trigger: 'scheduled',
  mode: 'improve',
  model: {
    instanceUuid: 'mock-instance',
    styleGuideUuid: 'mock-style-guide',
    temperature: 0.95,
    topK: 20,
    topP: 0.85
  },
  additionalInstructions: '',
  summary: {
    adCount: 7,
    adsWithValidationErrors: 0
  },
  ads: {
    google_ads: {
      '5550001111': {
        '20000001': {
          '30000011': {
            '700000000101': {
              current: {
                headlines: ['Boat Covers', 'Shop Boat Covers', 'Acme Marine'],
                descriptions: [
                  'Boat covers for every boat. Shop now.',
                  'Free shipping on select covers.'
                ]
              },
              generated: {
                headlines: [
                  'Boat Covers Built To Last',
                  'shop boat covers',
                  'Custom Fit Boat Covers',
                  'UV & Mildew Resistant',
                  'Free Shipping Over $99',
                  'Protect Your Boat All Year',
                  'Heavy Duty Marine Fabric',
                  'Fits Most Boat Models',
                  'Easy On, Easy Off Design',
                  'Trusted By Boaters Since 1990',
                  'Waterproof Boat Covers',
                  'Find Your Perfect Fit',
                  'Top Rated Boat Covers',
                  'Save On Boat Covers Today',
                  'Boat Covers In Stock Now'
                ],
                descriptions: [
                  'Keep your boat dry and clean with durable, custom-fit covers. Shop the full collection.',
                  'Marine-grade fabric stands up to sun, rain and salt. Free shipping on orders over $99.',
                  'Not sure what fits? Use our boat cover finder and get the right size in minutes.',
                  '30-day returns on every cover. Gear up for the season with Acme Marine.'
                ]
              },
              validationErrors: [],
              context: {
                'Campaign Name': 'Boat Covers',
                'Ad Group Name': 'Boat Covers - Custom',
                keywords: 'boat covers,custom boat covers'
              }
            },
            '700000000102': {
              flagged: MOCK_FLAGGED,
              current: {
                headlines: ['Pontoon Boat Covers', 'Waterproof Covers', 'Buy Online'],
                descriptions: [
                  'Waterproof covers for pontoon boats.',
                  'Shop our full cover collection.'
                ]
              },
              generated: {
                headlines: [
                  'Boat Covers Built To Last',
                  'Shop Marine Boat Covers',
                  'Custom Fit Boat Covers',
                  'UV & Mildew Resistant',
                  'Free Shipping Over $99',
                  'Protect Your Boat All Year',
                  'Heavy Duty Marine Fabric',
                  'Fits Most Boat Models',
                  'Easy On, Easy Off Design',
                  'Trusted By Boaters Since 1990',
                  'Waterproof Boat Covers',
                  'Find Your Perfect Fit'
                ],
                descriptions: [
                  'Keep your boat dry and clean with durable, custom-fit covers. Shop the full collection.',
                  'Marine-grade fabric stands up to sun, rain and salt. Free shipping on orders over $99.',
                  'Not sure what fits? Use our boat cover finder and get the right size in minutes.'
                ]
              },
              validationErrors: [],
              context: {
                'Campaign Name': 'Boat Covers',
                'Ad Group Name': 'Boat Covers - Custom',
                keywords: 'waterproof boat cover,pontoon boat cover'
              }
            }
          },
          '30000012': {
            '700000000103': {
              current: {
                headlines: ['V-Hull Boat Covers', 'Bass Boat Covers', 'Fast Shipping'],
                descriptions: ['Fits V-hull and bass boats.', 'Order today.']
              },
              generated: {
                headlines: [
                  'Custom Fit Boat Covers',
                  'UV & Mildew Resistant',
                  'Free Shipping Over $99',
                  'Protect Your Boat All Year',
                  'Heavy Duty Marine Fabric',
                  'Fits Most Boat Models',
                  'Easy On, Easy Off Design',
                  'Trusted By Boaters Since 1990',
                  'Waterproof Boat Covers',
                  'Find Your Perfect Fit',
                  'Top Rated Boat Covers',
                  'Save On Boat Covers Today',
                  'Boat Covers In Stock Now'
                ],
                descriptions: [
                  'Keep your boat dry and clean with durable, custom-fit covers. Shop the full collection.',
                  'Marine-grade fabric stands up to sun, rain and salt. Free shipping on orders over $99.',
                  'Not sure what fits? Use our boat cover finder and get the right size in minutes.',
                  '30-day returns on every cover. Gear up for the season with Acme Marine.'
                ]
              },
              validationErrors: [],
              context: {
                'Campaign Name': 'Boat Covers',
                'Ad Group Name': 'Boat Covers - Hull Type',
                keywords: 'v hull boat cover,bass boat cover'
              }
            }
          }
        },
        '20000002': {
          '30000021': {
            '700000000201': {
              current: {
                headlines: ['Boat Anchors', 'Shop Anchors Online', 'Acme Marine'],
                descriptions: ['Anchors for every size boat.', 'Free shipping available.']
              },
              generated: {
                headlines: [
                  'Anchors For Every Boat Size',
                  'Shop Boat Anchors Online',
                  'Danforth & Fluke Anchors',
                  'Anchor Kits With Rode',
                  'Secure Hold In Any Bottom',
                  'Stainless Steel Anchors',
                  'Free Shipping Over $99',
                  'Find The Right Anchor',
                  'Top Rated Marine Anchors',
                  'Anchor Up With Confidence',
                  'Rope, Chain & Hardware',
                  'Lightweight Folding Anchors',
                  'Anchors In Stock Now',
                  'Save On Anchors Today',
                  'Built For Rough Water'
                ],
                descriptions: [
                  'Find the right anchor for your boat and bottom type. Kits, chain and gear in stock.',
                  'Corrosion-resistant anchors trusted by boaters. Free shipping on orders over $99.',
                  'Not sure which anchor to pick? Our sizing guide matches the anchor to your boat.',
                  'Easy returns within 30 days. Shop anchors and rode at Acme Marine.'
                ]
              },
              validationErrors: [],
              names: { account: 'Acme Marine US', campaign: 'Boat Anchors', adGroup: 'Anchors' },
              context: {
                keywords: 'boat anchor,danforth anchor'
              }
            }
          },
          '30000022': {
            '700000000202': {
              flagged: MOCK_FLAGGED,
              warnings: MOCK_WARNINGS,
              current: {
                headlines: ['Anchor Kits', 'Anchors With Rode'],
                descriptions: ['Complete anchor kits in stock.', 'Shop now at Acme Marine.']
              },
              generated: {
                headlines: [
                  'Anchors For Every Boat Size',
                  'Shop Boat Anchors Online',
                  'Danforth & Fluke Anchors',
                  'Anchor Kits With Rode',
                  'Secure Hold In Any Bottom',
                  'Stainless Steel Anchors',
                  'Free Shipping Over $99',
                  'Find The Right Anchor',
                  'Top Rated Marine Anchors',
                  'Anchor Up With Confidence',
                  'Rope, Chain & Hardware',
                  'Lightweight Folding Anchors',
                  'Anchors In Stock Now'
                ],
                descriptions: [
                  'Find the right anchor for your boat and bottom type. Kits, chain and gear in stock.',
                  'Corrosion-resistant anchors trusted by boaters. Free shipping on orders over $99.',
                  'Not sure which anchor to pick? Our sizing guide matches the anchor to your boat.',
                  'Easy returns within 30 days. Shop anchors and rode at Acme Marine.'
                ]
              },
              validationErrors: [],
              names: {
                account: 'Acme Marine US',
                campaign: 'Boat Anchors',
                adGroup: 'Anchor Kits'
              },
              context: {
                keywords: 'anchor kit,anchor rode'
              }
            }
          }
        },
        '20000003': {
          '30000031': {
            '700000000301': {
              current: {
                headlines: ['Life Jackets', 'Coast Guard Approved', 'Shop Life Vests'],
                descriptions: ['Life jackets for the whole family.', 'Shop safety gear today.']
              },
              generated: {
                headlines: [
                  'Life Jackets For The Family',
                  'Shop Coast Guard Approved',
                  'Kids, Adult & Pet Vests',
                  'Comfort Fit Life Jackets',
                  'Stay Safe On The Water',
                  'Inflatable PFDs In Stock',
                  'Free Shipping Over $99',
                  'Find The Right Size',
                  'Top Rated Life Vests',
                  'Gear Up For Safe Boating',
                  'Paddle & Fishing Vests',
                  'Lightweight Mesh Vests',
                  'Life Jackets On Sale',
                  'Save On Safety Gear',
                  'Be Ready Before You Launch'
                ],
                descriptions: [
                  'Coast Guard approved life jackets for kids and adults. Find your perfect fit today.',
                  'Comfortable, breathable vests and inflatable PFDs. Free shipping on orders over $99.',
                  'Use our size guide to find a life jacket that fits right and stays on in the water.',
                  'Easy 30-day returns. Safety gear you can trust from Acme Marine.'
                ]
              },
              validationErrors: [],
              names: {
                account: 'Acme Marine US',
                campaign: 'Safety Gear',
                adGroup: 'Life Jackets'
              },
              context: {
                keywords: 'life jackets,coast guard approved life vest'
              }
            }
          }
        }
      }
    },
    bing: {
      '7770002222': {
        '40000001': {
          '50000011': {
            '800000000101': {
              current: {
                headlines: ['Boat Covers Online', 'Marine Boat Covers', 'Acme Marine'],
                descriptions: ['Durable covers for every boat.', 'Shop the collection.']
              },
              generated: {
                headlines: [
                  'Boat Covers Built To Last',
                  'Shop Marine Boat Covers',
                  'Custom Fit Boat Covers',
                  'UV & Mildew Resistant',
                  'Free Shipping Over $99',
                  'Protect Your Boat All Year',
                  'Heavy Duty Marine Fabric',
                  'Fits Most Boat Models',
                  'Easy On, Easy Off Design',
                  'Trusted By Boaters Since 1990',
                  'Waterproof Boat Covers',
                  'Find Your Perfect Fit',
                  'Top Rated Boat Covers',
                  'Save On Boat Covers Today'
                ],
                descriptions: [
                  'Keep your boat dry and clean with durable, custom-fit covers. Shop the full collection.',
                  'Marine-grade fabric stands up to sun, rain and salt. Free shipping on orders over $99.',
                  'Not sure what fits? Use our boat cover finder and get the right size in minutes.',
                  '30-day returns on every cover. Gear up for the season with Acme Marine.'
                ]
              },
              validationErrors: [],
              names: {
                account: 'Acme Marine Bing',
                campaign: 'Boat Covers - Bing',
                adGroup: 'Marine Covers'
              },
              context: {
                keywords: MANY_KEYWORDS
              }
            }
          }
        }
      }
    }
  }
};

export const MOCK_APPROVALS: CopyTigerRunApprovals = {
  run: {
    runId: 'mock-run',
    company: 'acmemarine',
    stage: 'internal_review',
    sentToClientAt: null,
    sentBy: null,
    clientSubmittedAt: null,
    clientSubmittedBy: null,
    createdAt: '2026-10-06T14:00:00Z'
  },
  approvals: [
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000011',
      adId: '700000000101',
      internalStatus: 'approved',
      internalReviewer: 'reviewer@example.com',
      internalReviewedAt: '2026-10-06T16:00:00Z',
      internalNote: null,
      clientStatus: 'approved',
      clientReviewer: 'client@example.com',
      clientReviewedAt: '2026-10-06T17:00:00Z',
      clientNote: null,
      lineRejections: [
        {
          stage: 'internal',
          field: 'headlines',
          index: 9,
          text: 'Trusted By Boaters Since 1990',
          note: "Can't verify the founding year"
        },
        {
          stage: 'client',
          field: 'headlines',
          index: 4,
          text: 'Free Shipping Over $99',
          note: 'Our free shipping threshold is $149'
        }
      ],
      modifiedAt: '2026-10-06T16:00:00Z'
    },
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000011',
      adId: '700000000102',
      internalStatus: 'approved',
      internalReviewer: 'reviewer@example.com',
      internalReviewedAt: '2026-10-06T16:00:00Z',
      internalNote: null,
      clientStatus: 'pending',
      clientReviewer: null,
      clientReviewedAt: null,
      clientNote: null,
      lineRejections: [
        {
          stage: 'internal',
          field: 'headlines',
          index: 4,
          text: 'Free Shipping Over $99',
          note: 'Promo ended'
        }
      ],
      modifiedAt: '2026-10-06T16:00:00Z'
    },
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000012',
      adId: '700000000103',
      internalStatus: 'rejected',
      internalReviewer: 'reviewer@example.com',
      internalReviewedAt: '2026-10-06T16:00:00Z',
      internalNote: 'Too many claims we cannot verify',
      clientStatus: 'pending',
      clientReviewer: null,
      clientReviewedAt: null,
      clientNote: null,
      lineRejections: [],
      modifiedAt: '2026-10-06T16:00:00Z'
    },
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000021',
      adId: '700000000201',
      internalStatus: 'approved',
      internalReviewer: 'reviewer@example.com',
      internalReviewedAt: '2026-10-06T16:00:00Z',
      internalNote: null,
      clientStatus: 'pending',
      clientReviewer: null,
      clientReviewedAt: null,
      clientNote: null,
      lineRejections: [],
      modifiedAt: '2026-10-06T16:00:00Z'
    },
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000022',
      adId: '700000000202',
      internalStatus: 'pending',
      internalReviewer: null,
      internalReviewedAt: null,
      internalNote: null,
      clientStatus: 'pending',
      clientReviewer: null,
      clientReviewedAt: null,
      clientNote: null,
      lineRejections: [],
      modifiedAt: null
    },
    {
      platform: 'google_ads',
      accountId: '5550001111',
      adGroupId: '30000031',
      adId: '700000000301',
      internalStatus: 'approved',
      internalReviewer: 'reviewer@example.com',
      internalReviewedAt: '2026-10-06T16:00:00Z',
      internalNote: null,
      clientStatus: 'rejected',
      clientReviewer: 'client@example.com',
      clientReviewedAt: '2026-10-06T17:00:00Z',
      clientNote: null,
      lineRejections: [],
      modifiedAt: '2026-10-06T16:00:00Z'
    },
    {
      platform: 'bing',
      accountId: '7770002222',
      adGroupId: '50000011',
      adId: '800000000101',
      internalStatus: 'pending',
      internalReviewer: null,
      internalReviewedAt: null,
      internalNote: null,
      clientStatus: 'pending',
      clientReviewer: null,
      clientReviewedAt: null,
      clientNote: null,
      lineRejections: [],
      modifiedAt: null
    }
  ]
};

// The same run after every ad got an internal decision and the run was sent to the client.
export const MOCK_SENT_APPROVALS: CopyTigerRunApprovals = {
  run: {
    ...MOCK_APPROVALS.run,
    stage: 'client_review',
    sentToClientAt: '2026-10-06T18:00:00Z',
    sentBy: 'reviewer@example.com'
  },
  approvals: MOCK_APPROVALS.approvals.map(approval =>
    approval.internalStatus === 'pending'
      ? {
          ...approval,
          internalStatus: 'approved',
          internalReviewer: 'reviewer@example.com',
          internalReviewedAt: '2026-10-06T17:30:00Z'
        }
      : approval
  )
};

// The sent run after the client decided every ad and submitted their review (ready to apply).
export const MOCK_SUBMITTED_APPROVALS: CopyTigerRunApprovals = {
  run: {
    ...MOCK_SENT_APPROVALS.run,
    stage: 'ready_to_apply',
    clientSubmittedAt: '2026-10-07T15:00:00Z',
    clientSubmittedBy: 'client@example.com'
  },
  approvals: MOCK_SENT_APPROVALS.approvals.map(approval => {
    if (approval.internalStatus !== 'approved') {
      return approval;
    }
    if (approval.clientStatus === 'rejected') {
      return { ...approval, clientNote: 'Too salesy for our brand' };
    }
    return approval.clientStatus === 'pending'
      ? {
          ...approval,
          clientStatus: 'approved',
          clientReviewer: 'client@example.com',
          clientReviewedAt: '2026-10-07T14:30:00Z'
        }
      : approval;
  })
};
